/**
 * InvoiceMaterialsEditor — the "Materials / line items" block of the
 * invoice editor, extracted from InvoiceEditorDialog.jsx (Jun 2026).
 * All state stays in the parent dialog and arrives as props.
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CustomColumnInputs } from '../ColumnLayoutEditor';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2 } from 'lucide-react';

export const InvoiceMaterialsEditor = ({
  materials,
  visibleMaterials,
  filterByWorker,
  setFilterByWorker,
  addMaterial,
  removeMaterial,
  updateMaterial,
  effectiveMarkup,
  money,
  qbItems,
  qbClasses,
  columns = [],
}) => (
  <div data-testid="invoice-materials-block">
    <div className="flex items-center justify-between mb-2">
      <Label className="text-xs uppercase tracking-wider text-slate-400">Materials / line items</Label>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={addMaterial}
        className="border-[#2D3748] bg-[#1A2433] text-white hover:bg-[#2D3748]"
        data-testid="invoice-add-material"
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> Add material
      </Button>
    </div>
    {materials.length === 0 ? (
      <div className="text-xs text-slate-500 italic py-3 px-3 border border-dashed border-[#2D3748] rounded-md">
        No materials added. Click "Add material" to bill parts separately from labor.
      </div>
    ) : visibleMaterials.length === 0 ? (
      <div className="text-xs text-amber-300 py-3 px-3 border border-dashed border-amber-700/50 rounded-md bg-amber-950/20">
        No materials from <strong>{filterByWorker}</strong> on this job. <button type="button" onClick={() => setFilterByWorker(null)} className="underline ml-1">Clear filter</button>
      </div>
    ) : (
      <div className="space-y-2">
        {/* Header row labels */}
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 px-1">
          <div className="col-span-4">Description</div>
          <div className="col-span-1 text-right">Qty</div>
          <div className="col-span-2 text-right">Cost</div>
          <div className="col-span-1 text-right">Markup</div>
          <div className="col-span-2 text-right">Unit price</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1"></div>
        </div>
        {visibleMaterials.map((m) => {
          const idx = m._origIdx;
          const q = parseFloat(m.quantity) || 0;
          const up = parseFloat(m.unit_price) || 0;
          const amt = q * up;
          return (
            <div key={m.id || idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`invoice-material-row-${idx}`}>
              <Input
                value={m.description || ''}
                onChange={(e) => updateMaterial(idx, 'description', e.target.value)}
                placeholder="e.g., 2×4 lumber"
                className="col-span-4 bg-[#1A2433] border-[#2D3748] text-white text-sm"
                data-testid={`invoice-material-desc-${idx}`}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={m.quantity}
                onChange={(e) => updateMaterial(idx, 'quantity', e.target.value)}
                placeholder="Qty"
                className="col-span-1 bg-[#1A2433] border-[#2D3748] text-white text-sm text-right"
                data-testid={`invoice-material-qty-${idx}`}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={m.cost == null ? '' : m.cost}
                onChange={(e) => updateMaterial(idx, 'cost', e.target.value)}
                placeholder="Cost"
                className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm text-right"
                data-testid={`invoice-material-cost-${idx}`}
                title="Your cost (internal — not shown on customer PDF)"
              />
              <Input
                type="number"
                step="0.1"
                min="0"
                value={m.markup_pct == null ? '' : m.markup_pct}
                onChange={(e) => updateMaterial(idx, 'markup_pct', e.target.value)}
                placeholder={`${effectiveMarkup}%`}
                className="col-span-1 bg-[#1A2433] border-[#2D3748] text-white text-sm text-right"
                data-testid={`invoice-material-markup-${idx}`}
                title="Per-row markup override (blank = use effective markup above)"
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={m.unit_price}
                onChange={(e) => updateMaterial(idx, 'unit_price', e.target.value)}
                placeholder="Unit $"
                className="col-span-2 bg-[#1A2433] border-[#2D3748] text-white text-sm text-right"
                data-testid={`invoice-material-price-${idx}`}
              />
              <div className="col-span-1 text-right text-sm text-slate-300 pt-2 font-medium">
                {money(amt)}
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeMaterial(idx)}
                className="col-span-1 text-rose-400 hover:text-rose-300"
                data-testid={`invoice-material-remove-${idx}`}
                title="Remove this line"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              {columns.some((c) => c.source === 'custom') && (
                <div className="col-span-12">
                  <CustomColumnInputs columns={columns} values={m.custom_fields} onChange={(v) => updateMaterial(idx, 'custom_fields', v)} testIdPrefix={`invoice-material-cf-${idx}`} compact />
                </div>
              )}
              {/* Per-line QBO Item + Class pickers (Feb 2026).
                  Only renders when QBO has been synced (lists are
                  non-empty). The Item dropdown maps directly to
                  the QBO invoice's Product/Service column; Class
                  falls back to project default when blank. */}
              {(qbItems.length > 0 || qbClasses.length > 0) && (
                <div className="col-span-12 grid grid-cols-12 gap-2 pl-1 pb-1">
                  {qbItems.length > 0 && (
                    <div className="col-span-6 flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 flex-shrink-0">QB Item</span>
                      <Select
                        value={m.qb_item_id || '__inherit__'}
                        onValueChange={(v) => {
                          if (v === '__inherit__') {
                            updateMaterial(idx, 'qb_item_id', null);
                            updateMaterial(idx, 'qb_item_name', null);
                          } else {
                            const it = qbItems.find((x) => x.qb_item_id === v);
                            updateMaterial(idx, 'qb_item_id', v);
                            updateMaterial(idx, 'qb_item_name', it?.name || null);
                          }
                        }}
                      >
                        <SelectTrigger
                          className="h-8 bg-[#1A2433] border-[#2D3748] text-white text-xs flex-1"
                          data-testid={`invoice-material-qbitem-${idx}`}
                        >
                          <SelectValue placeholder="Use project default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__inherit__">Use project default</SelectItem>
                          {qbItems.map((it) => (
                            <SelectItem key={it.qb_item_id} value={it.qb_item_id}>
                              {it.name}{it.type ? ` · ${it.type}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {qbClasses.length > 0 && (
                    <div className={`${qbItems.length > 0 ? 'col-span-6' : 'col-span-12'} flex items-center gap-1.5`}>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 flex-shrink-0">QB Class</span>
                      <Select
                        value={m.qb_class_id || '__inherit__'}
                        onValueChange={(v) => {
                          if (v === '__inherit__') {
                            updateMaterial(idx, 'qb_class_id', null);
                            updateMaterial(idx, 'qb_class_name', null);
                          } else {
                            const c = qbClasses.find((x) => x.qb_class_id === v);
                            updateMaterial(idx, 'qb_class_id', v);
                            updateMaterial(idx, 'qb_class_name', c?.name || null);
                          }
                        }}
                      >
                        <SelectTrigger
                          className="h-8 bg-[#1A2433] border-[#2D3748] text-white text-xs flex-1"
                          data-testid={`invoice-material-qbclass-${idx}`}
                        >
                          <SelectValue placeholder="Use project default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__inherit__">Use project default</SelectItem>
                          {qbClasses.map((c) => (
                            <SelectItem key={c.qb_class_id} value={c.qb_class_id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default InvoiceMaterialsEditor;
